<template>
  <v-app dark>
    <app-loading :overlay="overlay" />
    <app-drawer-left ref="drawer" @toggle="drawerOpen = $event" />
    <v-app-bar flat height="65" fixed app class="ps-sale-bar">
      <v-btn
        icon
        class="ps-drawer-toggle-btn ml-1"
        :aria-label="$t('toggle_sidebar') || 'Toggle sidebar'"
        @click.stop="$refs.drawer.toggleSidebar()"
      >
        <v-icon small>{{
          drawerOpen ? "fal fa-arrow-left" : "fal fa-arrow-right"
        }}</v-icon>
      </v-btn>
      <span v-if="pageTitle" class="page-header-title ml-3" :title="pageTitle">
        {{ pageTitle }}
      </span>

      <TextSelect
        class="sale-category-select ml-4"
        :dense="true"
        :placeholder="false"
        :items="categories"
        text="category_display"
        itemValue="id"
        v-model="product"
      />

      <v-autocomplete
        v-if="$vuetify.breakpoint.mdAndUp"
        ref="searchField"
          v-model="searchSelected"
          :search-input.sync="search"
          :items="searchSuggestions"
          :loading="searchLoading"
          outlined
          flat
          class="search-product ml-4"
          hide-details
          color="primary"
          :placeholder="$t('search_by_code')"
          dense
          hide-no-data
          hide-selected
          no-filter
          return-object
          item-text="name"
          item-value="id"
          :menu-props="{ contentClass: 'ps-sale-search-menu', offsetY: true }"
          @update:search-input="onSearchInput"
          @change="onSearchSelect"
          @keydown.enter.prevent="handleSearchSubmit"
          @focus="onSearchFieldFocus"
          @blur="onSearchFieldBlur"
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title class="ps-sale-search-item__name">{{
                item.name
              }}</v-list-item-title>
              <v-list-item-subtitle class="ps-sale-search-item__code">{{
                item.code
              }}</v-list-item-subtitle>
            </v-list-item-content>
          </template>
          <template v-slot:append>
            <!-- search icon (suffix position) -->
            <v-icon
              @click="handleSearchSubmit"
              small
              color="primary"
              class="ps-sale-search-btn"
              >fal fa-search</v-icon
            >
            <!-- clear icon (append position, shown while typing) -->
            <v-icon
              v-if="search"
              @click="clearSearchField"
              small
              class="ps-sale-clear-btn"
              :aria-label="$t('clear') || 'Clear'"
              >fal fa-times-circle</v-icon
            >
          </template>
        </v-autocomplete>

        <v-spacer />

        <span v-if="activeShift" class="ps-shift-note">
          {{ $t("shift_active") }} · {{ activeShift.clock_in_at | momentTime }}
        </span>

        <notification-bell />

        <v-btn
          v-if="$vuetify.breakpoint.mobile"
          icon
          @click.stop="$refs.drawerRight.drawer = !$refs.drawerRight.drawer"
          class="mr-2"
        >
          <v-badge
            :content="defineStore.cart.length.toString()"
            :value="defineStore.cart.length"
            color="red"
            overlap
            bordered
          >
            <v-icon>fal fa-shopping-cart</v-icon>
          </v-badge>
        </v-btn>
    </v-app-bar>

    <v-main class="overflow-y-auto">
      <alertExpired />
      <kept-router-view />
    </v-main>

    <app-drawer-right ref="drawerRight" />

    <announcement-popup />

    <!-- Customer screen auto-open prompt (shown when popup is blocked) -->
    <v-snackbar
      v-model="customerScreenPrompt"
      :timeout="-1"
      bottom
      right
      color="teal darken-1"
      elevation="4"
      max-width="380"
    >
      <v-icon left small>fal fa-tv</v-icon>
      {{
        $t("customer_screen_auto_open_prompt") ||
        "Second screen detected — open customer display?"
      }}
      <template v-slot:action="{ attrs }">
        <v-btn text small v-bind="attrs" @click="launchCustomerScreen">
          {{ $t("open") || "Open" }}
        </v-btn>
        <v-btn
          icon
          x-small
          v-bind="attrs"
          @click="customerScreenPrompt = false"
        >
          <v-icon x-small>fal fa-times</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <ProductSelectionModal
      v-if="selectedProduct"
      :show.sync="showVariationModal"
      :product="selectedProduct"
      @update:show="handleCloseVariationModal"
    />
    <DynamicPriceModal
      v-if="selectedProductForDynamicPrice"
      :show.sync="showDynamicPriceModal"
      :product="selectedProductForDynamicPrice"
      @update:show="handleCloseDynamicPriceModal"
    />
    <PackQuantityModal
      v-if="selectedProductForPack"
      :show.sync="showPackModal"
      :product="selectedProductForPack"
      @update:show="handleClosePackModal"
    />

    <!-- Visually hidden: hardware scanners type here (keyboard emulation).
         Do not use aria-hidden — this input is focused on sale/table routes. -->
    <input
      ref="scannerInput"
      v-model="scannerBuffer"
      class="scanner-capture"
      type="text"
      autocomplete="off"
      autocorrect="off"
      spellcheck="false"
      tabindex="-1"
      aria-label="Barcode scanner"
      @keydown.enter.prevent="handleScannerEnter"
      @input="handleScannerInput"
      @blur="handleScannerBlur"
    />
  </v-app>
</template>

<script>
import KeptRouterView from "@/components/global/KeptRouterView.vue";
import alertExpired from "@/components/alertExpired.vue";
import AppDrawerLeft from "@/components/layouts/drawerLeft";
import AppDrawerRight from "@/components/layouts/drawerRight";
import AnnouncementPopup from "@/components/AnnouncementPopup";
import NotificationBell from "@/components/NotificationBell";
import { mapGetters, mapMutations, mapActions } from "vuex";
import { toast, confirm } from "@/controllers/alert";
import { myMixin } from "@/mixin/main";
import { setUser, checkToken, fetchRole } from "@/controllers/auth";
import TextSelect from "@/components/global/TextSelect";
import { mainStore } from "@/stores/modules/cart";
import ProductSelectionModal from "@/components/products/ProductSelectionModal.vue";
import DynamicPriceModal from "@/components/products/DynamicPriceModal.vue";
import PackQuantityModal from "@/components/products/PackQuantityModal.vue";
import customerScreenMonitor from "@/utils/customerScreenMonitor";

// Module-level flag — survives layout remounts on route changes,
// resets only on full page refresh (which is the correct "startup" signal).
let _customerScreenAutoOpened = false;

export default {
  components: {
    KeptRouterView,
    AppDrawerLeft,
    AppDrawerRight,
    AnnouncementPopup,
    NotificationBell,
    TextSelect,
    alertExpired,
    ProductSelectionModal,
    DynamicPriceModal,
    PackQuantityModal,
  },
  mixins: [myMixin],
  data() {
    return {
      defineStore: mainStore(),
      drawerOpen: true,
      eye: false,
      search: "",
      searchSelected: null,
      searchSuggestions: [],
      searchLoading: false,
      searchDebounceTimer: null,
      product: 0,
      showVariationModal: false,
      selectedProduct: null,
      showDynamicPriceModal: false,
      selectedProductForDynamicPrice: null,
      showPackModal: false,
      selectedProductForPack: null,
      currentTicketId: null,
      // Barcode detection state
      barcodeBuffer: "",
      scannerBuffer: "",
      scannerInputTimeout: null,
      lastKeyTime: 0,
      barcodeTimeout: null,
      isSearchFieldFocused: false,
      barcodeDetectionEnabled: true,
      isProcessingBarcode: false,
      customerScreenPrompt: false,
    };
  },
  watch: {
    product(val) {
      // Commit category ID to Vuex
      this.SET_SELECTED_CATEGORY_ID(val);
      if (val === 0) {
        return this.searchProducts();
      }
      this.filterProduct(val);
      this.scrollToTop();
    },
    $route(to) {
      // Keep the barcode scanner input focused on sale/table routes.
      // (Sidebar is no longer auto-hidden on navigation.)
      if (["/sale", "/table", "/mobile/table"].includes(to.path)) {
        this.$nextTick(() => {
          this.focusScannerInput();
        });
      }
    },
  },

  created() {
    checkToken(this);
    const themes = localStorage.getItem("darkMode");
    if (themes) {
      this.$vuetify.theme.dark = themes.length === 4;
    }
  },
  mounted() {
    setUser(this);
    fetchRole(this);
    this.fetchCategory();
    const hide = localStorage.getItem("hide");
    this.eye = hide === "true" ? true : false;

    // Add global keydown listener for barcode detection (fallback)
    this.barcodeKeydownHandler = this.handleBarcodeKeydown.bind(this);
    document.addEventListener("keydown", this.barcodeKeydownHandler);

    this.$root.$on("focus-scanner", this.focusScannerInput);

    this.setupScannerOverlayObserver();

    // Auto-open customer screen on dual-screen setups if setting is enabled
    this.$nextTick(() => {
      setTimeout(() => this.tryAutoOpenCustomerScreen(), 2000);
      if (this.isScannerRoute()) {
        this.focusScannerInput();
      }
    });
  },

  beforeDestroy() {
    // Cleanup PubNub subscription
    if (this.pubnub) {
      this.pubnub.unsubscribeAll();
    }

    // Remove barcode detection listener
    if (this.barcodeKeydownHandler) {
      document.removeEventListener("keydown", this.barcodeKeydownHandler);
    }

    this.$root.$off("focus-scanner", this.focusScannerInput);
    this.cleanupScannerOverlayObserver();

    // Clear any pending timeout
    if (this.barcodeTimeout) {
      clearTimeout(this.barcodeTimeout);
    }
    if (this.scannerInputTimeout) {
      clearTimeout(this.scannerInputTimeout);
    }
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  },
  computed: {
    ...mapGetters({
      category: "index/category",
      user: "auth/user",
      mySale: "index/mySale",
      overlay: "index/overlay",
      activeShift: "index/activeShift",
    }),
    categories() {
      if (!this.category?.length) {
        return [];
      }
      const all = {
        id: 0,
        la_name: "ທັງໝົດ",
        en_name: "All",
        th_name: "ทั้งหมด",
        cn_name: "全部",
      };
      return this.mapCategoriesForSelect([all, ...this.category]);
    },
    posName() {
      if (
        this.user != null &&
        this.user != undefined &&
        this.user.stores != null &&
        this.user.stores != undefined &&
        this.user.stores.store_id != null &&
        this.user.stores.store_id != undefined &&
        this.user.stores.store_id.name != null &&
        this.user.stores.store_id.name
      ) {
        return this.user.stores.store_id.name;
      }
    },
  },
  methods: {
    async tryAutoOpenCustomerScreen() {
      // Only fire once per page load, even if layout remounts on route changes
      if (_customerScreenAutoOpened) return;

      const config = customerScreenMonitor.getConfig();
      if (!config.autoOpenOnStartup) return;

      if (this.$route && this.$route.path === "/customer-screen") return;

      // window.screen.isExtended: true when multiple screens are connected (Chrome 93+/Edge 93+)
      const hasSecondScreen = window.screen.isExtended === true;
      if (!hasSecondScreen) return;

      // Mark as done before the async call so concurrent remounts can't race
      _customerScreenAutoOpened = true;

      try {
        const result = await customerScreenMonitor.openCustomerScreen(
          "/customer-screen",
        );
        if (!result.success) {
          this.customerScreenPrompt = true;
        }
      } catch (e) {
        this.customerScreenPrompt = true;
      }
    },

    async launchCustomerScreen() {
      this.customerScreenPrompt = false;
      try {
        await customerScreenMonitor.openCustomerScreen("/customer-screen");
      } catch (e) {
        window.open("/customer-screen", "_blank");
      }
    },

    ...mapActions({
      fetch: "index/fetch",
    }),
    ...mapMutations({
      SET_OVERLAY: "index/SET_OVERLAY",
      SET_SELECTED_CATEGORY_ID: "index/SET_SELECTED_CATEGORY_ID",
    }),

    async fetchCategory() {
      const body = {
        url: "categories",
        that: this,
        commit: "SET_CATEGORY",
      };
      await this.fetch(body);
    },
    async filterProduct({ id }) {
      // Commit category ID to Vuex
      this.SET_SELECTED_CATEGORY_ID(id);
      this.SET_OVERLAY(true);
      const tableId = this.$route.query.tableId || this.$route.query.table_id;
      let url = `products_paginate?cat_id=${id}`;
      if (tableId) url += `&table_id=${tableId}`;
      const body = {
        that: this,
        url,
        commit: "SET_PRODUCT",
      };
      await this.fetch(body);
      this.SET_OVERLAY(false);
    },
    async searchProducts(val) {
      if (this.isProcessingBarcode && val) {
        return;
      }
      this.SET_OVERLAY(true);
      try {
        if (val) {
          this.isProcessingBarcode = true;
          const { data } = await this.$http.get(
            `get_cart?q=${encodeURIComponent(val)}`,
          );
          if (data.status && data.data != null && data.data != undefined) {
            this.clearSearchField();
            await this.addProductFromResponse(data.data);
          } else if (data && data.status === false) {
            // Handle business logic error (product not found)
            const errorMessage = data.message || "no_data";
            await toast(this, errorMessage, "error");
          }
        } else {
          this.clearSearchField();
          // Reset category when clearing search
          this.SET_SELECTED_CATEGORY_ID(0);
          const tableId =
            this.$route.query.tableId || this.$route.query.table_id;
          const url = tableId
            ? `products_paginate?table_id=${tableId}`
            : "products_paginate";
          const body = {
            that: this,
            url,
            commit: "SET_PRODUCT",
          };
          await this.fetch(body);
        }
      } catch (error) {
        console.error("searchProducts", error);
        // Handle 404 errors with toast (axios interceptor skips 404, so we handle here)
        if (error.response && error.response.status === 404) {
          const errorMessage = error.response.data?.message || "no_data";
          await toast(this, errorMessage, "error");
        } else {
          // Handle other errors
          await toast(this, "error_occurred", "error");
        }
      } finally {
        this.isProcessingBarcode = false;
        this.SET_OVERLAY(false);
        if (this.isScannerRoute()) {
          this.focusScannerInput();
        }
      }
    },
    clearSearchField() {
      this.search = "";
      this.searchSelected = null;
      this.searchSuggestions = [];
    },
    onSearchInput(val) {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      if (!val || !String(val).trim()) {
        this.searchSuggestions = [];
        return;
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.fetchSearchSuggestions(val);
      }, 300);
    },
    async fetchSearchSuggestions(query) {
      const q = String(query || "").trim();
      if (!q) {
        this.searchSuggestions = [];
        return;
      }
      this.searchLoading = true;
      try {
        const { data } = await this.$http.get(
          `products_paginate?q=${encodeURIComponent(q)}&per_page=10&page=1`,
        );
        if (data && data.status && data.data && Array.isArray(data.data.data)) {
          this.searchSuggestions = data.data.data;
        } else {
          this.searchSuggestions = [];
        }
      } catch (error) {
        console.error("fetchSearchSuggestions", error);
        this.searchSuggestions = [];
      } finally {
        this.searchLoading = false;
      }
    },
    resolveSearchSelection(query) {
      const q = String(query || "")
        .trim()
        .toLowerCase();
      const items = this.searchSuggestions || [];
      if (!q || !items.length) return null;

      const byCode = items.find(
        (product) => String(product.code || "").toLowerCase() === q,
      );
      if (byCode) return byCode;

      const byName = items.find(
        (product) => String(product.name || "").toLowerCase() === q,
      );
      if (byName) return byName;

      if (items.length === 1) return items[0];
      return null;
    },
    async handleSearchSubmit() {
      const query = String(this.search || "").trim();
      if (!query) {
        await this.searchProducts("");
        return;
      }

      await this.fetchSearchSuggestions(query);
      const resolved = this.resolveSearchSelection(query);
      if (resolved) {
        await this.selectSearchProduct(resolved);
        return;
      }

      if (this.searchSuggestions.length > 1) {
        await toast(this, "select_product_from_list", "warning");
        return;
      }

      await this.searchProducts(query);
    },
    async onSearchSelect(product) {
      if (!product) return;
      await this.selectSearchProduct(product);
    },
    async selectSearchProduct(product) {
      if (!product || !product.code) return;
      await this.searchProducts(product.code);
    },
    async addProductFromResponse(product) {
      if (
        product.has_variations ||
        (product.product_variants && product.product_variants.length > 0) ||
        (product.product_options && product.product_options.length > 0)
      ) {
        this.selectedProduct = product;
        this.showVariationModal = true;
      } else if (product.dynamic_pricing) {
        this.selectedProductForDynamicPrice = product;
        this.showDynamicPriceModal = true;
      } else if (product.size && product.size.unit === "pack") {
        this.selectedProductForPack = product;
        this.showPackModal = true;
      } else {
        const body = {
          that: this,
          payload: product,
        };
        this.defineStore.addToCart(body);
        await toast(this, "add_to_cart_success");
      }
    },
    scrollToTop() {
      window.scrollTo({ top: 0 });
    },
    async setHide() {
      this.eye = !this.eye;
      localStorage.setItem("hide", this.eye);
    },
    handleCloseVariationModal() {
      this.showVariationModal = false;
      this.selectedProduct = null;
      this.focusScannerInput();
    },
    handleCloseDynamicPriceModal() {
      this.showDynamicPriceModal = false;
      this.selectedProductForDynamicPrice = null;
      this.focusScannerInput();
    },
    handleClosePackModal() {
      this.showPackModal = false;
      this.selectedProductForPack = null;
      this.focusScannerInput();
    },

    isScannerRoute() {
      const paths = ["/sale", "/table", "/mobile/table"];
      return paths.includes(this.$route?.path);
    },

    isScannerFocusBlocked() {
      if (document.querySelector(".swal2-container.swal2-shown")) {
        return true;
      }
      if (document.querySelector(".v-dialog--active")) {
        return true;
      }
      const app = document.getElementById("app");
      if (app && app.getAttribute("aria-hidden") === "true") {
        return true;
      }
      return false;
    },

    /**
     * True when the product search field (app bar) is focused or still owns focus.
     * Prefer this over isSearchFieldFocused alone — blur/focus races with the scanner.
     */
    isProductSearchActive() {
      if (this.isSearchFieldFocused) {
        return true;
      }
      const active = document.activeElement;
      if (!active) {
        return false;
      }
      if (active.closest && active.closest(".search-product")) {
        return true;
      }
      const searchCmp = this.$refs.searchField;
      const searchEl = searchCmp && (searchCmp.$refs?.input || searchCmp.$el);
      if (searchEl && (active === searchEl || searchEl.contains?.(active))) {
        return true;
      }
      return false;
    },

    shouldBlockScannerFocus() {
      if (this.isScannerFocusBlocked()) {
        return true;
      }
      if (this.isProductSearchActive()) {
        return true;
      }
      const activeElement = document.activeElement;
      if (!activeElement) {
        return false;
      }
      // Scanner capture itself must not block re-focus / keep-focus logic
      if (
        activeElement === this.$refs.scannerInput ||
        (activeElement.classList &&
          activeElement.classList.contains("scanner-capture"))
      ) {
        return false;
      }
      const tagName = activeElement.tagName?.toLowerCase();
      if (
        tagName === "input" ||
        tagName === "textarea" ||
        activeElement.isContentEditable ||
        activeElement.closest(".v-dialog")
      ) {
        return true;
      }
      return false;
    },

    blurScannerInput() {
      const el = this.$refs.scannerInput;
      if (
        el &&
        document.activeElement === el &&
        typeof el.blur === "function"
      ) {
        el.blur();
      }
    },

    releaseScannerFocusIfBlocked() {
      if (this.isScannerFocusBlocked()) {
        this.blurScannerInput();
      }
    },

    setupScannerOverlayObserver() {
      if (typeof MutationObserver === "undefined") {
        return;
      }
      this.scannerOverlayObserver = new MutationObserver(() => {
        this.releaseScannerFocusIfBlocked();
      });
      this.scannerOverlayObserver.observe(document.body, {
        attributes: true,
        subtree: true,
        attributeFilter: ["class", "aria-hidden", "style"],
      });
    },

    cleanupScannerOverlayObserver() {
      if (this.scannerOverlayObserver) {
        this.scannerOverlayObserver.disconnect();
        this.scannerOverlayObserver = null;
      }
    },

    focusScannerInput() {
      if (!this.barcodeDetectionEnabled || !this.isScannerRoute()) {
        return;
      }
      // Overlay/dialog open: release focus so aria-hidden ancestors stay valid
      if (this.isScannerFocusBlocked()) {
        this.blurScannerInput();
        return;
      }
      // Never steal focus from the product search field
      if (this.isProductSearchActive() || this.shouldBlockScannerFocus()) {
        return;
      }
      this.$nextTick(() => {
        if (this.isScannerFocusBlocked()) {
          this.blurScannerInput();
          return;
        }
        if (this.isProductSearchActive()) {
          return;
        }
        const el = this.$refs.scannerInput;
        if (el && typeof el.focus === "function") {
          el.focus();
        }
      });
    },

    handleScannerEnter() {
      this.submitScannerBuffer();
    },

    handleScannerInput() {
      if (this.scannerInputTimeout) {
        clearTimeout(this.scannerInputTimeout);
      }
      // Scanners without Enter suffix: process after brief pause
      this.scannerInputTimeout = setTimeout(() => {
        this.submitScannerBuffer();
      }, 200);
    },

    handleScannerBlur() {
      if (!this.isScannerRoute()) {
        return;
      }
      setTimeout(() => {
        if (
          !this.isScannerFocusBlocked() &&
          !this.isProductSearchActive() &&
          !this.shouldBlockScannerFocus()
        ) {
          this.focusScannerInput();
        }
      }, 100);
    },

    submitScannerBuffer() {
      if (this.isProcessingBarcode) {
        return;
      }
      if (this.scannerInputTimeout) {
        clearTimeout(this.scannerInputTimeout);
        this.scannerInputTimeout = null;
      }
      const barcode = (this.scannerBuffer || "").trim();
      this.scannerBuffer = "";
      if (barcode.length >= 4) {
        this.processBarcode(barcode);
      }
    },

    // Barcode detection methods (keydown fallback when scanner input loses focus)
    handleBarcodeKeydown(event) {
      // Don't process if barcode detection is disabled
      if (!this.barcodeDetectionEnabled) {
        return;
      }

      // Don't process if search field is focused (let normal input work)
      if (this.isProductSearchActive()) {
        return;
      }

      // Primary capture: hidden scanner input
      const scannerEl = this.$refs.scannerInput;
      if (scannerEl && document.activeElement === scannerEl) {
        return;
      }

      // Don't process if ANY input, textarea, or contenteditable element is focused
      // This covers modal inputs, payment fields, and any other form elements
      const activeElement = document.activeElement;
      if (activeElement) {
        const tagName = activeElement.tagName?.toLowerCase();
        if (
          tagName === "input" ||
          tagName === "textarea" ||
          activeElement.isContentEditable ||
          activeElement.closest(".v-dialog")
        ) {
          return;
        }
      }

      // Ignore modifier keys and navigation keys
      if (
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        event.shiftKey ||
        event.key === "Tab" ||
        event.key === "Escape" ||
        event.key === "ArrowUp" ||
        event.key === "ArrowDown" ||
        event.key === "ArrowLeft" ||
        event.key === "ArrowRight" ||
        event.key === "Home" ||
        event.key === "End" ||
        event.key === "PageUp" ||
        event.key === "PageDown"
      ) {
        return;
      }

      const currentTime = Date.now();
      const timeSinceLastKey = currentTime - this.lastKeyTime;

      // If Enter key is pressed, process the barcode
      if (event.key === "Enter") {
        event.preventDefault();
        if (this.barcodeBuffer.length >= 4) {
          // Minimum barcode length is 4 characters
          this.processBarcode(this.barcodeBuffer);
        }
        this.resetBarcodeBuffer();
        return;
      }

      // If too much time has passed since last key (>150ms), reset buffer
      // This indicates human typing, not barcode scanner
      if (timeSinceLastKey > 150 && this.barcodeBuffer.length > 0) {
        this.resetBarcodeBuffer();
      }

      // Add character to buffer if it's a printable character
      if (event.key && event.key.length === 1) {
        // Clear any existing timeout
        if (this.barcodeTimeout) {
          clearTimeout(this.barcodeTimeout);
        }

        // Add character to buffer
        this.barcodeBuffer += event.key;
        this.lastKeyTime = currentTime;

        // Set timeout to reset buffer if no more keys come (scanner finished without Enter)
        this.barcodeTimeout = setTimeout(() => {
          if (this.barcodeBuffer.length >= 4) {
            // Some scanners don't send Enter, so process after timeout
            this.processBarcode(this.barcodeBuffer);
          }
          this.resetBarcodeBuffer();
        }, 200); // 200ms timeout for scanners without Enter
      }
    },

    async processBarcode(barcode) {
      if (this.isProcessingBarcode) {
        return;
      }
      const trimmed = (barcode || "").trim();
      if (!trimmed || trimmed.length < 4) {
        return;
      }
      this.search = trimmed;
      await this.searchProducts(trimmed);
    },

    resetBarcodeBuffer() {
      this.barcodeBuffer = "";
      this.lastKeyTime = 0;
      if (this.barcodeTimeout) {
        clearTimeout(this.barcodeTimeout);
        this.barcodeTimeout = null;
      }
    },

    onSearchFieldFocus() {
      this.isSearchFieldFocused = true;
      // Clear any pending barcode buffer when user manually focuses
      this.resetBarcodeBuffer();
      this.scannerBuffer = "";
      // Ensure scanner does not keep focus after clicking search
      this.blurScannerInput();
    },

    onSearchFieldBlur() {
      this.isSearchFieldFocused = false;
      if (this.isScannerRoute()) {
        // Delay so clearable / icon clicks inside the field can re-focus search first
        setTimeout(() => {
          if (!this.isProductSearchActive() && !this.isScannerFocusBlocked()) {
            this.focusScannerInput();
          }
        }, 120);
      }
    },
  },
};
</script>

<style scoped>
.page-header-title {
  font-size: 16px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.92);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.theme--light .page-header-title {
  color: #141021;
}

.search-product {
  flex: 1 1 240px;
  min-width: 200px;
  max-width: 420px;
}

.ps-shift-note {
  font-size: 11.5px;
  color: #64748b;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  margin-right: 12px;
}

/* ---- Sale page: category dropdown + product search (brutal) ---- */

/* shared input shell: cream fill, light border, brand focus */
.sale-category-select ::v-deep .v-input__slot,
.search-product ::v-deep .v-input__slot {
  border: 1px solid #cbd5e1 !important;
  border-radius: 12px !important;
  min-height: 44px !important;
  box-shadow: none !important;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.sale-category-select ::v-deep .v-input.v-input--is-focused .v-input__slot,
.search-product.v-input--is-focused ::v-deep .v-input__slot {
  border-color: #5b39c9 !important;
}

/* hide the outlined fieldset so our border is the only one */
.sale-category-select ::v-deep .v-input__slot fieldset,
.search-product ::v-deep .v-input__slot fieldset {
  border: none !important;
}

.sale-category-select ::v-deep .v-input__slot input,
.search-product ::v-deep .v-input__slot input {
  color: #141021;
  font-weight: 700;
  font-size: 14px;
}

/* category dropdown: text + chevron */
.sale-category-select ::v-deep .v-select__selection {
  color: #141021;
  font-weight: 500;
  font-size: 14px;
}

.sale-category-select ::v-deep .v-select__slot .v-icon {
  color: #5b39c9 !important;
}

/* search icon button (suffix position) */
.ps-sale-search-btn {
  cursor: pointer;
  transition: transform 0.15s ease;
}

.ps-sale-search-btn:hover {
  transform: scale(1.15);
}

/* clear icon button (append position, while typing) */
.search-product ::v-deep .v-input__append-inner {
  gap: 6px;
  align-self: center !important;
  display: flex;
  align-items: center;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  padding-left: 4px;
}

.search-product ::v-deep .v-input__append-inner .v-icon {
  line-height: 1;
}

.ps-sale-clear-btn {
  color: #94a3b8 !important;
  cursor: pointer;
  transition: color 0.15s ease, transform 0.15s ease;
}

.ps-sale-clear-btn:hover {
  color: #dc2626 !important;
  transform: scale(1.15);
}

.ps-sale-search-item__name {
  font-weight: 700;
  font-size: 14px;
  color: #141021;
}

.ps-sale-search-item__code {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
}

.ps-drawer-toggle-btn {
  width: 32px !important;
  height: 32px !important;
  border: 1px solid #e2e8f0 !important;
  background: #ffffff;
  border-radius: 8px !important;
  color: #94a3b8;
  transition: color 0.15s ease, border-color 0.15s ease,
    background-color 0.15s ease;
}

.ps-drawer-toggle-btn:hover {
  color: #5b39c9 !important;
  border-color: #5b39c9 !important;
  background: #f3f0fc !important;
}

.theme--dark.ps-sale-bar,
.theme--dark .ps-sale-bar {
  border-bottom: 1px solid #2f333a !important;
}

.theme--dark .ps-shift-note {
  color: #94a3b8;
}

.theme--dark .sale-category-select ::v-deep .v-input__slot,
.theme--dark .search-product ::v-deep .v-input__slot {
  background-color: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .sale-category-select ::v-deep .v-input.v-input--is-focused .v-input__slot,
.theme--dark .search-product.v-input--is-focused ::v-deep .v-input__slot {
  border-color: #7c5cea !important;
}

.theme--dark .sale-category-select ::v-deep .v-input__slot input,
.theme--dark .search-product ::v-deep .v-input__slot input,
.theme--dark .sale-category-select ::v-deep .v-select__selection {
  color: #f1f5f9 !important;
}

.theme--dark .sale-category-select ::v-deep .v-select__slot .v-icon {
  color: #9a8cf0 !important;
}

.theme--dark .ps-sale-search-item__name {
  color: #f1f5f9;
}

.theme--dark .ps-drawer-toggle-btn {
  background: transparent;
  border-color: #2f333a !important;
  color: rgba(226, 232, 240, 0.6);
}

.theme--dark .ps-drawer-toggle-btn:hover {
  color: #7c5cea !important;
  border-color: #7c5cea !important;
  background: rgba(91, 57, 201, 0.2) !important;
}

.scanner-capture {
  position: fixed;
  left: -9999px;
  top: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
  padding: 0;
  margin: 0;
  border: 0;
}
</style>
